import React, { useState, useCallback, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SettingsServicePaymentType } from '../../../../api/types/settings/payments/settings-service-payment-type';
import appApi from '../../../../api/app-api';
import dateFormat from '../../../../utils/date-format';

const ITEMS_PER_PAGE = 10;

export interface PaymentTypeSelectionDialogProps {
  visible: boolean;
  selectedType: SettingsServicePaymentType | null;
  onHide: () => void;
  onApprove: (selectedType: SettingsServicePaymentType | null) => void;
}

export const PaymentTypeSelectionDialog: React.FC<
  PaymentTypeSelectionDialogProps
> = ({ visible, selectedType, onHide, onApprove }) => {
  const [paymentTypes, setPaymentTypes] = useState<
    SettingsServicePaymentType[]
  >([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(ITEMS_PER_PAGE);
  const [tempSelectedType, setTempSelectedType] =
    useState<SettingsServicePaymentType | null>(null);

  const loadPaymentTypes = useCallback(
    async (offset: number, limit: number) => {
      setTableLoading(true);
      try {
        const response = await appApi.getPaymentServiceTypesPaginated(
          offset,
          limit,
        );
        if (response.data.body) {
          setPaymentTypes(response.data.body.data);
          setTotalRecords(response.data.body.total);
        }
      } catch (error) {
        console.error('Error loading payment types:', error);
      } finally {
        setTableLoading(false);
      }
    },
    [],
  );

  // Load data when dialog becomes visible
  useEffect(() => {
    if (visible) {
      setTempSelectedType(selectedType);
      setFirst(0);
      loadPaymentTypes(0, ITEMS_PER_PAGE);
    }
  }, [visible, selectedType, loadPaymentTypes]);

  const onPageChange = (event: DataTablePageEvent) => {
    setFirst(event.first);
    setRows(event.rows);
    loadPaymentTypes(event.first, event.rows);
  };

  const handleApprove = () => {
    onApprove(tempSelectedType);
  };

  const handleCancel = () => {
    setTempSelectedType(null);
    onHide();
  };

  return (
    <Dialog
      header="בחירת סוג שרות"
      visible={visible}
      onHide={handleCancel}
      style={{ width: '700px' }}
      footer={
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            severity="secondary"
            onClick={handleCancel}
          />
          <Button
            label="אישור"
            icon="pi pi-check"
            onClick={handleApprove}
            disabled={!tempSelectedType}
          />
        </div>
      }
    >
      <DataTable
        value={paymentTypes}
        loading={tableLoading}
        paginator
        lazy
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPage={onPageChange}
        rowsPerPageOptions={[5, 10, 25]}
        selectionMode="single"
        selection={tempSelectedType}
        onSelectionChange={(e) =>
          setTempSelectedType(e.value as SettingsServicePaymentType)
        }
        dataKey="id"
        emptyMessage={
          <div style={{ textAlign: 'right' }}>לא נמצאו סוגי שרות</div>
        }
        tableStyle={{ direction: 'rtl' }}
      >
        <Column
          selectionMode="single"
          headerStyle={{ width: '3rem' }}
          style={{ textAlign: 'center' }}
        />
        <Column
          field="paymentServiceTypeName"
          header="שם סוג השרות"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="createdAt"
          header="תאריך יצירה"
          style={{ textAlign: 'right' }}
          body={(rowData: SettingsServicePaymentType) =>
            dateFormat.displayFormatDate(new Date(rowData.createdAt))
          }
        />
      </DataTable>
    </Dialog>
  );
};
